import React, { useState, useEffect, useRef } from 'react';
import {
  Plus,
  Trash2,
  Move,
  StickyNote,
  Square,
  CheckSquare,
  Sparkles,
  Save,
  ZoomIn,
  ZoomOut,
  Maximize2,
  GitBranch,
  ArrowRight,
  ExternalLink,
  Tag,
  Circle,
  FolderKanban,
  Check,
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext.tsx';
import { Task, WhiteboardElement } from '../../../shared/types.ts';
import { PriorityBadge } from '../common/Badge.tsx';

interface WhiteboardViewProps {
  projectId: string;
  tasks: Task[];
  onTaskClick?: (task: Task) => void;
  onConvertNoteToTask?: (title: string, description: string) => void;
}

const NOTE_COLORS = [
  '#fef08a', // yellow
  '#bbf7d0', // green
  '#fed7aa', // orange
  '#bfdbfe', // blue
  '#fbcfe8', // pink
  '#e9d5ff', // purple
];

interface MindMapConnection {
  fromId: string;
  toId: string;
}

export const WhiteboardView: React.FC<WhiteboardViewProps> = ({
  projectId,
  tasks,
  onTaskClick,
  onConvertNoteToTask,
}) => {
  const { token, user } = useAuth();
  const [elements, setElements] = useState<WhiteboardElement[]>([]);
  const [connections, setConnections] = useState<MindMapConnection[]>([
    { fromId: 'note-root', toId: 'note-1' },
    { fromId: 'note-root', toId: 'note-2' },
  ]);
  const [isSaving, setIsSaving] = useState(false);
  const [scale, setScale] = useState(1);
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const [dragOffset, setDragOffset] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
  const [isAiBrainstorming, setIsAiBrainstorming] = useState(false);
  const [connectSourceId, setConnectSourceId] = useState<string | null>(null);

  const containerRef = useRef<HTMLDivElement>(null);

  // Fetch whiteboard
  const fetchWhiteboard = async () => {
    if (!token) return;
    try {
      const res = await fetch(`/api/features/whiteboards/${projectId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        if (data.whiteboard && Array.isArray(data.whiteboard.elements) && data.whiteboard.elements.length > 0) {
          setElements(data.whiteboard.elements);
        } else {
          initDefaultTemplate();
        }
      }
    } catch (err) {
      console.error('Failed to load whiteboard:', err);
      initDefaultTemplate();
    }
  };

  const initDefaultTemplate = () => {
    const defaults: WhiteboardElement[] = [
      {
        id: 'note-root',
        type: 'sticky_note',
        x: 420,
        y: 60,
        width: 260,
        height: 120,
        content: '🧠 Project Core Mind Map\nCentral architecture & feature ideation hub.',
        color: '#bfdbfe',
      },
      {
        id: 'note-1',
        type: 'sticky_note',
        x: 140,
        y: 240,
        width: 220,
        height: 140,
        content: '💡 UI / UX Experience:\n- Neo-Grotesque design\n- Sub-second switching\n- Dense spreadsheet view',
        color: '#fef08a',
      },
      {
        id: 'note-2',
        type: 'sticky_note',
        x: 700,
        y: 240,
        width: 220,
        height: 140,
        content: '🎯 Execution Strategy:\n- Critical path tracking\n- WebSocket video calls\n- Deep proofing timelines',
        color: '#bbf7d0',
      },
    ];

    // Auto-link first 2 tasks if available
    if (tasks.length > 0) {
      defaults.push({
        id: `task-node-${tasks[0].id}`,
        type: 'task_node',
        x: 420,
        y: 440,
        width: 250,
        height: 130,
        content: tasks[0].title,
        taskId: tasks[0].id,
        color: '#ffffff',
      });
    }

    setElements(defaults);
  };

  useEffect(() => {
    fetchWhiteboard();
  }, [projectId, token]);

  const saveWhiteboard = async (elementsToSave = elements) => {
    if (!token) return;
    setIsSaving(true);
    try {
      await fetch('/api/features/whiteboards', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          projectId,
          title: 'Project Brainstorm & Mind Map Canvas',
          elements: elementsToSave,
        }),
      });
    } catch (err) {
      console.error('Failed to save whiteboard:', err);
    } finally {
      setIsSaving(false);
    }
  };

  const handleAddNote = () => {
    const randomColor = NOTE_COLORS[Math.floor(Math.random() * NOTE_COLORS.length)];
    const newElement: WhiteboardElement = {
      id: `note-${Date.now()}`,
      type: 'sticky_note',
      x: 200 + Math.random() * 140,
      y: 180 + Math.random() * 140,
      width: 220,
      height: 140,
      content: 'New brainstorm idea...',
      color: randomColor,
    };
    const updated = [...elements, newElement];
    setElements(updated);
    saveWhiteboard(updated);
  };

  const handleAddTaskNode = (task: Task) => {
    if (elements.some((el) => el.taskId === task.id)) return;

    const newElement: WhiteboardElement = {
      id: `task-node-${task.id}`,
      type: 'task_node',
      x: 280 + Math.random() * 100,
      y: 280 + Math.random() * 100,
      width: 250,
      height: 130,
      content: task.title,
      taskId: task.id,
      color: '#ffffff',
    };
    const updated = [...elements, newElement];
    setElements(updated);
    saveWhiteboard(updated);
  };

  const handleRemoveElement = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    const updated = elements.filter((el) => el.id !== id);
    setElements(updated);
    setConnections((prev) => prev.filter((c) => c.fromId !== id && c.toId !== id));
    saveWhiteboard(updated);
  };

  const handleContentChange = (id: string, text: string) => {
    setElements((prev) =>
      prev.map((el) => (el.id === id ? { ...el, content: text } : el))
    );
  };

  const handleColorChange = (id: string, color: string) => {
    const updated = elements.map((el) => (el.id === id ? { ...el, color } : el));
    setElements(updated);
    saveWhiteboard(updated);
  };

  // Convert Sticky Note directly into a Live Project Task
  const handleConvertToTask = async (note: WhiteboardElement) => {
    if (!token || user?.role === 'viewer' || user?.role === 'guest') return;
    try {
      const title = note.content.split('\n')[0].replace(/^[^\w\s]+/, '').trim() || 'Brainstorm Action Item';
      const description = note.content;

      const res = await fetch('/api/tasks', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
          ...(user?.id ? { 'x-demo-user-id': user.id } : {}),
        },
        body: JSON.stringify({
          projectId,
          title,
          description,
          status: 'todo',
          priority: 'medium',
          assigneeId: user?.id || null,
          assignorId: user?.id || null,
        }),
      });

      if (res.ok) {
        const d = await res.json();
        if (d.task) {
          window.dispatchEvent(new CustomEvent('task-created', { detail: d.task }));
        }
        // Morph sticky note into a live task node
        const updated = elements.map((el) =>
          el.id === note.id
            ? {
                ...el,
                type: 'task_node' as const,
                taskId: d.task.id,
                content: d.task.title,
                color: '#ffffff',
              }
            : el
        );
        setElements(updated);
        saveWhiteboard(updated);
      }
    } catch (e) {
      console.error('Failed to convert note to task:', e);
    }
  };

  // Connect Mind Map branches between nodes
  const handleToggleConnect = (nodeId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!connectSourceId) {
      setConnectSourceId(nodeId);
    } else {
      if (connectSourceId !== nodeId) {
        setConnections((prev) => {
          if (prev.some((c) => c.fromId === connectSourceId && c.toId === nodeId)) return prev;
          return [...prev, { fromId: connectSourceId, toId: nodeId }];
        });
      }
      setConnectSourceId(null);
    }
  };

  const [isTaskDropdownOpen, setIsTaskDropdownOpen] = useState(false);

  // Mouse Dragging logic
  const handleMouseDown = (id: string, e: React.MouseEvent) => {
    if ((e.target as HTMLElement).tagName === 'TEXTAREA' || (e.target as HTMLElement).tagName === 'BUTTON') return;
    setDraggingId(id);
    const target = elements.find((el) => el.id === id);
    if (target && containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      const mouseX = (e.clientX - rect.left) / scale;
      const mouseY = (e.clientY - rect.top) / scale;
      setDragOffset({
        x: mouseX - target.x,
        y: mouseY - target.y,
      });
    }
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!draggingId || !containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const mouseX = (e.clientX - rect.left) / scale;
    const mouseY = (e.clientY - rect.top) / scale;

    const newX = Math.max(10, mouseX - dragOffset.x);
    const newY = Math.max(10, mouseY - dragOffset.y);

    setElements((prev) =>
      prev.map((el) =>
        el.id === draggingId ? { ...el, x: Math.round(newX), y: Math.round(newY) } : el
      )
    );
  };

  const handleMouseUp = () => {
    if (draggingId) {
      setDraggingId(null);
      saveWhiteboard();
    }
  };

  // Touch Dragging logic for mobile screens
  const handleTouchStart = (id: string, e: React.TouchEvent) => {
    if ((e.target as HTMLElement).tagName === 'TEXTAREA' || (e.target as HTMLElement).tagName === 'BUTTON') return;
    if (e.touches.length === 1) {
      const touch = e.touches[0];
      setDraggingId(id);
      const target = elements.find((el) => el.id === id);
      if (target && containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        const touchX = (touch.clientX - rect.left) / scale;
        const touchY = (touch.clientY - rect.top) / scale;
        setDragOffset({
          x: touchX - target.x,
          y: touchY - target.y,
        });
      }
    }
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!draggingId || !containerRef.current || e.touches.length !== 1) return;
    const touch = e.touches[0];
    const rect = containerRef.current.getBoundingClientRect();
    const touchX = (touch.clientX - rect.left) / scale;
    const touchY = (touch.clientY - rect.top) / scale;

    const newX = Math.max(10, touchX - dragOffset.x);
    const newY = Math.max(10, touchY - dragOffset.y);

    setElements((prev) =>
      prev.map((el) =>
        el.id === draggingId ? { ...el, x: Math.round(newX), y: Math.round(newY) } : el
      )
    );
  };

  const handleTouchEnd = () => {
    if (draggingId) {
      setDraggingId(null);
      saveWhiteboard();
    }
  };

  // AI Brainstorm generation
  const handleAIBrainstorm = () => {
    setIsAiBrainstorming(true);
    setTimeout(() => {
      const aiIdeas = [
        '⚡ Implement Progressive Disclosure: default minimalist checklist for quick onboarding.',
        '📊 Add cross-project mirroring: display key tasks in executive portfolio without duplication.',
        '🛡️ Add Skill-Matrix AI matching: match task requirements against engineer competency tags.',
      ];
      const newItems: WhiteboardElement[] = aiIdeas.map((idea, idx) => ({
        id: `ai-note-${Date.now()}-${idx}`,
        type: 'sticky_note',
        x: 260 + idx * 260,
        y: 380,
        width: 230,
        height: 140,
        content: idea,
        color: NOTE_COLORS[(idx + 2) % NOTE_COLORS.length],
      }));

      const updated = [...elements, ...newItems];
      setElements(updated);
      saveWhiteboard(updated);
      setIsAiBrainstorming(false);
    }, 800);
  };

  return (
    <div className="bg-white dark:bg-surface-900 rounded-3xl shadow-neo-card border border-[#d5d8de]/60 dark:border-surface-800 overflow-hidden flex flex-col h-[560px] sm:h-[750px] font-sans">
      {/* Top Toolbar */}
      <div className="p-3.5 sm:p-4 border-b border-surface-200 dark:border-surface-800 flex flex-wrap items-center justify-between gap-3 bg-[#f8f9fa] dark:bg-surface-850">
        <div className="flex flex-wrap items-center gap-2">
          {/* Add Sticky Note */}
          <button
            onClick={handleAddNote}
            className="flex items-center space-x-1.5 px-3 py-1.5 rounded-xl text-xs font-bold bg-pulse-gradient text-white shadow-pulse hover:opacity-95 transition-all"
          >
            <StickyNote className="w-3.5 h-3.5" />
            <span>Add Sticky Note</span>
          </button>

          {/* Add Task Node Dropdown */}
          <div className="relative">
            <button
              onClick={() => setIsTaskDropdownOpen(!isTaskDropdownOpen)}
              className="flex items-center space-x-1.5 px-3 py-1.5 rounded-xl text-xs font-bold bg-surface-100 dark:bg-surface-800 text-surface-800 dark:text-surface-200 hover:bg-surface-200 transition-colors"
            >
              <FolderKanban className="w-3.5 h-3.5 text-brand-600" />
              <span>Link Task ({tasks.length})</span>
            </button>
            {isTaskDropdownOpen && (
              <>
                <div
                  className="fixed inset-0 z-20"
                  onClick={() => setIsTaskDropdownOpen(false)}
                />
                <div className="absolute left-0 top-full mt-1 w-72 bg-white dark:bg-surface-800 border border-surface-200 dark:border-surface-700 rounded-2xl shadow-xl z-30 max-h-56 overflow-y-auto p-1.5 space-y-1">
                  {tasks.map((task) => (
                    <div
                      key={task.id}
                      onClick={() => {
                        handleAddTaskNode(task);
                        setIsTaskDropdownOpen(false);
                      }}
                      className="px-3 py-2 rounded-xl text-xs text-surface-700 dark:text-surface-300 hover:bg-brand-50 dark:hover:bg-brand-950/40 hover:text-brand-600 cursor-pointer truncate font-medium flex items-center justify-between"
                    >
                      <span className="truncate">{task.title}</span>
                      <Plus className="w-3.5 h-3.5 shrink-0 ml-1 opacity-60" />
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>

          {/* AI Brainstorm */}
          <button
            onClick={handleAIBrainstorm}
            disabled={isAiBrainstorming}
            className="flex items-center space-x-1.5 px-3 py-1.5 rounded-xl text-xs font-bold bg-gradient-to-r from-purple-600 to-indigo-600 text-white shadow-sm hover:opacity-90 transition-all"
          >
            <Sparkles className="w-3.5 h-3.5" />
            <span>{isAiBrainstorming ? 'Generating Nodes...' : 'AI Brainstorm Ideas'}</span>
          </button>
        </div>

        {/* Zoom & Canvas State */}
        <div className="flex items-center space-x-3">
          {connectSourceId && (
            <div className="flex items-center space-x-1.5 px-3 py-1 rounded-full bg-amber-500/20 text-amber-700 dark:text-amber-300 text-xs font-bold border border-amber-500/30 animate-pulse">
              <GitBranch className="w-3.5 h-3.5" />
              <span>Click any node to connect branch</span>
            </div>
          )}

          <div className="flex items-center space-x-1 bg-white dark:bg-surface-800 border border-surface-200 dark:border-surface-700 rounded-xl p-1">
            <button
              onClick={() => setScale((s) => Math.max(0.6, s - 0.1))}
              className="p-1 text-surface-500 hover:text-surface-900 rounded"
              title="Zoom out"
            >
              <ZoomOut className="w-3.5 h-3.5" />
            </button>
            <span className="text-[11px] font-mono font-bold px-2 text-surface-600 dark:text-surface-300">
              {Math.round(scale * 100)}%
            </span>
            <button
              onClick={() => setScale((s) => Math.min(1.6, s + 0.1))}
              className="p-1 text-surface-500 hover:text-surface-900 rounded"
              title="Zoom in"
            >
              <ZoomIn className="w-3.5 h-3.5" />
            </button>
            <button
              onClick={() => setScale(1)}
              className="p-1 text-surface-500 hover:text-surface-900 rounded"
              title="Reset Zoom"
            >
              <Maximize2 className="w-3.5 h-3.5" />
            </button>
          </div>

          <button
            onClick={() => saveWhiteboard()}
            disabled={isSaving}
            className="px-3.5 py-1.5 rounded-xl text-xs font-bold bg-[#121417] text-white hover:bg-black transition-colors"
          >
            <Save className="w-3.5 h-3.5 inline mr-1" />
            <span>{isSaving ? 'Saving...' : 'Save Board'}</span>
          </button>
        </div>
      </div>

      {/* Interactive Infinite Canvas */}
      <div
        ref={containerRef}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onWheel={(e) => {
          if (Math.abs(e.deltaX) > Math.abs(e.deltaY) || e.deltaY === 0) return;
          // Sideways only with Shift held; reaching the bottom must not scroll the canvas sideways
          const el = e.currentTarget;
          const canScrollHorizontally =
            (e.deltaY > 0 && el.scrollLeft + el.clientWidth < el.scrollWidth - 2) ||
            (e.deltaY < 0 && el.scrollLeft > 1);

          if (e.shiftKey && canScrollHorizontally) {
            e.preventDefault();
            el.scrollLeft += e.deltaY;
          }
        }}
        className="flex-1 relative overflow-auto touch-scroll bg-[#f8fafc] dark:bg-[#0b1120] cursor-grab active:cursor-grabbing select-none"
        style={{
          backgroundImage:
            'radial-gradient(circle, rgba(148, 163, 184, 0.3) 1px, transparent 1px)',
          backgroundSize: '24px 24px',
        }}
      >
        <div
          className="min-w-[2200px] min-h-[1600px] relative transition-transform duration-75 origin-top-left"
          style={{ transform: `scale(${scale})` }}
        >
          {/* SVG Mind Map Branch Connecting Lines */}
          <svg className="absolute inset-0 w-full h-full pointer-events-none z-0">
            {connections.map((conn, idx) => {
              const fromEl = elements.find((e) => e.id === conn.fromId);
              const toEl = elements.find((e) => e.id === conn.toId);
              if (!fromEl || !toEl) return null;

              const x1 = fromEl.x + (fromEl.width || 220) / 2;
              const y1 = fromEl.y + (fromEl.height || 140) / 2;
              const x2 = toEl.x + (toEl.width || 220) / 2;
              const y2 = toEl.y + (toEl.height || 140) / 2;

              return (
                <g key={idx}>
                  <line
                    x1={x1}
                    y1={y1}
                    x2={x2}
                    y2={y2}
                    stroke="#94a3b8"
                    strokeWidth="2.5"
                    strokeDasharray="4 4"
                  />
                  <circle cx={(x1 + x2) / 2} cy={(y1 + y2) / 2} r="4" fill="#3b82f6" />
                </g>
              );
            })}
          </svg>

          {/* Interactive Elements / Mind Map Nodes */}
          {elements.map((el) => {
            const isNote = el.type === 'note' || el.type === 'sticky_note';
            const matchedTask = tasks.find((t) => t.id === el.taskId);
            const isConnectSource = connectSourceId === el.id;

            return (
              <div
                key={el.id}
                onMouseDown={(e) => handleMouseDown(el.id, e)}
                onTouchStart={(e) => handleTouchStart(el.id, e)}
                style={{
                  left: `${el.x}px`,
                  top: `${el.y}px`,
                  width: `${el.width || 240}px`,
                  backgroundColor: el.color || '#fef08a',
                }}
                className={`absolute p-3.5 rounded-3xl shadow-neo border cursor-move transition-shadow hover:shadow-xl group z-10 ${
                  isConnectSource
                    ? 'ring-4 ring-amber-400 border-amber-500'
                    : isNote
                    ? 'border-black/10 text-surface-900'
                    : 'bg-white dark:bg-surface-800 border-surface-200 dark:border-surface-700 text-surface-900 dark:text-surface-100'
                }`}
              >
                {/* Node Top Header & Branch Connect Tool */}
                <div className="flex items-center justify-between pb-2 mb-2 border-b border-black/5 dark:border-white/5">
                  <div className="flex items-center space-x-1.5">
                    <Move className="w-3 h-3 opacity-40 group-hover:opacity-100" />
                    <span className="text-[10px] font-extrabold uppercase tracking-wider opacity-70">
                      {isNote ? 'Sticky Note' : 'Task Node'}
                    </span>
                  </div>

                  <div className="flex items-center space-x-1 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity">
                    {/* Connect Mind Map Branch */}
                    <button
                      onClick={(e) => handleToggleConnect(el.id, e)}
                      title="Connect Mind Map Branch"
                      className="p-1 rounded-md text-surface-600 hover:text-brand-600 hover:bg-black/5 dark:hover:bg-white/10"
                    >
                      <GitBranch className="w-3 h-3" />
                    </button>

                    {/* Color picker for notes */}
                    {isNote && (
                      <div className="flex items-center space-x-1 mr-1">
                        {NOTE_COLORS.map((c) => (
                          <button
                            key={c}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleColorChange(el.id, c);
                            }}
                            style={{ backgroundColor: c }}
                            className={`w-2.5 h-2.5 rounded-full border border-black/20 transition-transform ${
                              el.color === c ? 'scale-125 ring-1 ring-black/40' : 'hover:scale-110'
                            }`}
                          />
                        ))}
                      </div>
                    )}

                    <button
                      onClick={(e) => handleRemoveElement(el.id, e)}
                      className="p-1 rounded text-black/50 hover:text-rose-600 hover:bg-black/5 dark:text-white/50 dark:hover:text-rose-400"
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                </div>

                {/* Node Content */}
                {isNote ? (
                  <div onMouseDown={(e) => e.stopPropagation()} onTouchStart={(e) => e.stopPropagation()}>
                    <textarea
                      value={el.content || ''}
                      onChange={(e) => handleContentChange(el.id, e.target.value)}
                      onBlur={() => saveWhiteboard()}
                      rows={4}
                      placeholder="Type brainstorming idea or notes here..."
                      className="w-full bg-transparent border-0 resize-none text-xs text-surface-900 leading-relaxed font-sans focus:outline-none focus:ring-1 focus:ring-black/10 rounded-lg p-1 placeholder-surface-500/50 cursor-text select-text"
                    />

                    {/* Convert to Task CTA button */}
                    <div className="mt-2 pt-2 border-t border-black/5 flex items-center justify-between">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleConvertToTask(el);
                        }}
                        className="px-2 py-1 rounded-lg bg-pulse-gradient text-white text-[10px] font-bold hover:opacity-95 shadow-sm transition-all flex items-center space-x-1"
                        title="Convert this idea into a project task"
                      >
                        <ArrowRight className="w-3 h-3 stroke-[2.5]" />
                        <span>Convert to Task</span>
                      </button>
                    </div>
                  </div>
                ) : (
                  <div>
                    <h5 className="font-extrabold text-xs mb-1.5 text-surface-900 dark:text-surface-100 line-clamp-2">
                      {el.content}
                    </h5>

                    {matchedTask && (
                      <div className="mt-2 pt-2 border-t border-surface-100 dark:border-surface-700/60 flex items-center justify-between">
                        <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-surface-100 dark:bg-surface-700 text-surface-700 dark:text-surface-300">
                          {matchedTask.status.replace('_', ' ')}
                        </span>

                        {onTaskClick && (
                          <button
                            onClick={() => onTaskClick(matchedTask)}
                            className="text-[11px] font-bold text-brand-600 dark:text-brand-400 hover:underline flex items-center space-x-0.5"
                          >
                            <span>Open Task</span>
                            <ExternalLink className="w-3 h-3 ml-0.5" />
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};
